import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Spinner, Badge, Collapse, Modal } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaChevronDown, FaChevronUp, FaSave, FaFilter, FaSync } from 'react-icons/fa';
import { useNotifications } from '../context/NotificationContext';
import { getAllLenderSettings, saveLenderSettings, deleteLenderSetting } from '../api/lenderSettingsApi';
import AddLenderModal from '../components/AddLenderModal';

const LenderSettings = () => {
  // State for lenders
  const [lenders, setLenders] = useState([]);
  const [filteredLenders, setFilteredLenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // State for UI
  const [filter, setFilter] = useState('all'); // all, working-capital, equipment-financing
  const [expandedLenders, setExpandedLenders] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  
  // State for form data
  const [formData, setFormData] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  
  // Notifications
  const { success, error: notifyError, info } = useNotifications();
  
  // Load lender settings on component mount
  useEffect(() => {
    loadLenderSettings();
  }, []);
  
  // Apply filter when filter or lenders change
  useEffect(() => {
    applyFilter();
  }, [filter, lenders]);
  
  /**
   * Load all lender settings from the API
   */
  const loadLenderSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const lenderData = await getAllLenderSettings();
      setLenders(lenderData);
      
      // Initialize form data
      const initialFormData = {};
      lenderData.forEach(lender => {
        initialFormData[lender._id] = { ...lender };
      });
      setFormData(initialFormData);
      
      console.log(`Loaded ${lenderData.length} lender settings`);
      
    } catch (err) {
      console.error('Error loading lender settings:', err);
      setError(err);
      notifyError('Failed to load lender settings', 'Error');
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Apply the current filter to the lenders list
   */
  const applyFilter = () => {
    if (filter === 'all') {
      setFilteredLenders(lenders);
    } else {
      setFilteredLenders(lenders.filter(lender => lender.financingType === filter));
    }
  };
  
  /**
   * Handle form field changes
   */
  const handleFieldChange = (lenderId, fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [lenderId]: {
        ...prev[lenderId],
        [fieldName]: value
      }
    }));
    setHasChanges(true);
  };
  
  /**
   * Toggle expanded state for a lender
   */
  const toggleExpanded = (lenderId) => {
    setExpandedLenders(prev => ({
      ...prev,
      [lenderId]: !prev[lenderId]
    }));
  };
  
  /**
   * Save all lender settings
   */
  const handleSaveAll = async () => {
    try {
      setSaving(true);
      
      // Convert form data to array format expected by API
      const lendersToSave = Object.values(formData);
      
      // Save lender settings
      const result = await saveLenderSettings(lendersToSave);
      
      // Reload data to get latest from server
      await loadLenderSettings();
      
      setHasChanges(false);
      
      if (result.errors && result.errors.length > 0) {
        // Show warnings for any errors
        result.errors.forEach(errorMsg => {
          notifyError(errorMsg, 'Warning');
        });
        success(`Saved ${result.data.length} lender(s) with some warnings`, 'Partial Success');
      } else {
        success(`Successfully saved ${result.data.length} lender setting(s)`, 'Success');
      }
      
    } catch (err) {
      console.error('Error saving lender settings:', err);
      notifyError(err, 'Save Failed');
    } finally {
      setSaving(false);
    }
  };
  
  /**
   * Delete a specific lender
   */
  const handleDeleteLender = async (lenderId) => {
    const lender = lenders.find(l => l._id === lenderId);
    if (!lender) return;
    
    try {
      await deleteLenderSetting(lenderId);
      
      // Remove from local state
      setLenders(prev => prev.filter(l => l._id !== lenderId));
      setFormData(prev => {
        const updated = { ...prev };
        delete updated[lenderId];
        return updated;
      });
      
      success(`Successfully deleted ${lender.lenderName}`, 'Deleted');
      setShowDeleteConfirm(null);
      
    } catch (err) {
      console.error('Error deleting lender:', err);
      notifyError(err, 'Delete Failed');
    }
  };
  
  /**
   * Handle new lender creation
   */
  const handleLenderCreated = (newLender) => {
    setLenders(prev => [...prev, newLender]);
    setFormData(prev => ({
      ...prev,
      [newLender._id]: { ...newLender }
    }));
    setShowAddModal(false);
    success(`Successfully created ${newLender.lenderName}`, 'Created');
  };
  
  /**
   * Get status badge variant based on lender properties
   */
  const getStatusBadge = (lender) => {
    if (!lender.active) {
      return <Badge bg="secondary">Inactive</Badge>;
    }
    
    const badges = [
      <Badge key="active" bg="success">Active</Badge>
    ];
    
    if (lender.emailOnly) {
      badges.push(<Badge key="email" bg="info" className="ms-1">Email</Badge>);
    }
    
    badges.push(<Badge key="tier" bg="outline-primary" className="ms-1">Tier {lender.tier}</Badge>);
    
    return badges;
  };
  
  /**
   * Get financing type display name
   */
  const getFinancingTypeDisplay = (type) => {
    switch (type) {
      case 'working-capital':
        return 'Working Capital';
      case 'equipment-financing':
        return 'Equipment Financing';
      default:
        return type;
    }
  };

  return (
    <Container fluid className="py-4">
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Lender Settings Management</h2>
            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                onClick={loadLenderSettings}
                disabled={loading}
              >
                <FaSync className={loading ? 'fa-spin' : ''} /> Refresh
              </Button>
              <Button
                variant="primary"
                onClick={() => setShowAddModal(true)}
                disabled={loading}
              >
                <FaPlus /> Add Email Lender
              </Button>
              <Button
                variant="success"
                onClick={handleSaveAll}
                disabled={!hasChanges || saving}
              >
                {saving ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FaSave /> Save All Changes
                  </>
                )}
              </Button>
            </div>
          </div>
          
          {/* Filter Section */}
          <Card className="mb-4">
            <Card.Body>
              <div className="d-flex align-items-center gap-3">
                <FaFilter />
                <strong>Filter by Financing Type:</strong>
                <Form.Select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  style={{ width: 'auto' }}
                >
                  <option value="all">All Lenders ({lenders.length})</option>
                  <option value="working-capital">
                    Working Capital ({lenders.filter(l => l.financingType === 'working-capital').length})
                  </option>
                  <option value="equipment-financing">
                    Equipment Financing ({lenders.filter(l => l.financingType === 'equipment-financing').length})
                  </option>
                </Form.Select>
              </div>
            </Card.Body>
          </Card>
          
          {/* Error Display */}
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              <strong>Error:</strong> {error}
            </Alert>
          )}
          
          {/* Loading Spinner */}
          {loading && (
            <div className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <div className="mt-2">Loading lender settings...</div>
            </div>
          )}
          
          {/* Lenders List */}
          {!loading && (
            <div className="lenders-list">
              {filteredLenders.length === 0 ? (
                <Alert variant="info">
                  {filter === 'all' 
                    ? 'No lenders configured yet. Click "Add Email Lender" to get started.'
                    : `No lenders found for ${getFinancingTypeDisplay(filter)}.`
                  }
                </Alert>
              ) : (
                filteredLenders.map(lender => {
                  const lenderFormData = formData[lender._id] || lender;
                  const isExpanded = expandedLenders[lender._id];
                  
                  return (
                    <Card key={lender._id} className="mb-3">
                      <Card.Header 
                        className="d-flex justify-content-between align-items-center cursor-pointer"
                        onClick={() => toggleExpanded(lender._id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <strong>{lender.lenderName}</strong>
                          <small className="text-muted">ID: {lender.uniqueLenderId}</small>
                          <div className="d-flex gap-1">
                            {getStatusBadge(lender)}
                          </div>
                          <Badge bg="outline-secondary">
                            {getFinancingTypeDisplay(lender.financingType)}
                          </Badge>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowDeleteConfirm(lender._id);
                            }}
                          >
                            <FaTrash />
                          </Button>
                          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </div>
                      </Card.Header>
                      
                      <Collapse in={isExpanded}>
                        <div>
                          <Card.Body>
                            <Form>
                              <Row>
                                {/* Basic Settings */}
                                <Col md={6}>
                                  <h6 className="mb-3">Basic Settings</h6>
                                  
                                  <Form.Group className="mb-3">
                                    <Form.Label>Lender Name *</Form.Label>
                                    <Form.Control
                                      type="text"
                                      value={lenderFormData.lenderName || ''}
                                      onChange={(e) => handleFieldChange(lender._id, 'lenderName', e.target.value)}
                                      required
                                    />
                                  </Form.Group>
                                  
                                  <Form.Group className="mb-3">
                                    <Form.Label>Financing Type *</Form.Label>
                                    <Form.Select
                                      value={lenderFormData.financingType || ''}
                                      onChange={(e) => handleFieldChange(lender._id, 'financingType', e.target.value)}
                                      required
                                    >
                                      <option value="">Select Type</option>
                                      <option value="working-capital">Working Capital</option>
                                      <option value="equipment-financing">Equipment Financing</option>
                                    </Form.Select>
                                  </Form.Group>
                                  
                                  <Row>
                                    <Col sm={6}>
                                      <Form.Group className="mb-3">
                                        <Form.Label>Tier</Form.Label>
                                        <Form.Select
                                          value={lenderFormData.tier || 3}
                                          onChange={(e) => handleFieldChange(lender._id, 'tier', parseInt(e.target.value))}
                                        >
                                          <option value={1}>Tier 1 (Highest Priority)</option>
                                          <option value={2}>Tier 2 (Medium Priority)</option>
                                          <option value={3}>Tier 3 (Lowest Priority)</option>
                                        </Form.Select>
                                      </Form.Group>
                                    </Col>
                                    <Col sm={6}>
                                      <Form.Group className="mb-3">
                                        <Form.Label>Status</Form.Label>
                                        <div className="mt-2">
                                          <Form.Check
                                            type="switch"
                                            id={`active-${lender._id}`}
                                            label="Active"
                                            checked={lenderFormData.active !== false}
                                            onChange={(e) => handleFieldChange(lender._id, 'active', e.target.checked)}
                                          />
                                        </div>
                                      </Form.Group>
                                    </Col>
                                  </Row>
                                </Col>
                                
                                {/* Email Settings */}
                                <Col md={6}>
                                  <h6 className="mb-3">Email Settings</h6>
                                  
                                  <Form.Group className="mb-3">
                                    <Form.Label>Email Address *</Form.Label>
                                    <Form.Control
                                      type="email"
                                      value={lenderFormData.emailToAddress || ''}
                                      onChange={(e) => handleFieldChange(lender._id, 'emailToAddress', e.target.value)}
                                      placeholder="lender@example.com"
                                      required
                                    />
                                  </Form.Group>
                                  
                                  <Form.Group className="mb-3">
                                    <Form.Label>CC Email Address (Optional)</Form.Label>
                                    <Form.Control
                                      type="email"
                                      value={lenderFormData.emailCcAddress || ''}
                                      onChange={(e) => handleFieldChange(lender._id, 'emailCcAddress', e.target.value)}
                                      placeholder="cc@example.com"
                                    />
                                    <Form.Text className="text-muted">
                                      Separate multiple emails with semicolons
                                    </Form.Text>
                                  </Form.Group>
                                  
                                  {/* Security Settings */}
                                  <div className="mt-4 pt-3 border-top">
                                    <h6 className="mb-3">🔒 Security Settings</h6>
                                    
                                    <Form.Group className="mb-3">
                                      <Form.Check
                                        type="switch"
                                        id={`watermark-${lender._id}`}
                                        label="Enable PDF Watermarking"
                                        checked={lenderFormData.watermarkEnabled || false}
                                        onChange={(e) => handleFieldChange(lender._id, 'watermarkEnabled', e.target.checked)}
                                      />
                                      <Form.Text className="text-muted">
                                        Adds diagonal "CONFIDENTIAL" watermark with lender ID to all PDF files
                                      </Form.Text>
                                    </Form.Group>
                                    
                                    <Form.Group className="mb-3">
                                      <Form.Check
                                        type="switch"
                                        id={`scramble-${lender._id}`}
                                        label="Scramble Contact Information"
                                        checked={lenderFormData.contactInfoScramble || false}
                                        onChange={(e) => handleFieldChange(lender._id, 'contactInfoScramble', e.target.checked)}
                                      />
                                      <Form.Text className="text-muted">
                                        Modifies phone numbers and emails to track potential data leaks
                                      </Form.Text>
                                    </Form.Group>
                                  </div>

                                </Col>
                              </Row>
                              
                              {/* Email Templates */}
                              <Row className="mt-4">
                                <Col>
                                  <h6 className="mb-3">Email Templates</h6>
                                  <p className="text-muted small">
                                    Available placeholders: {'{businessName}'}, {'{lenderName}'}, {'{requestedAmount}'}, {'{clientName}'}, {'{dealNotes}'}
                                  </p>
                                  
                                  <Form.Group className="mb-3">
                                    <Form.Label>Email Subject</Form.Label>
                                    <Form.Control
                                      type="text"
                                      value={lenderFormData.emailSubject || ''}
                                      onChange={(e) => handleFieldChange(lender._id, 'emailSubject', e.target.value)}
                                      placeholder="New Submission For Review - {businessName}"
                                    />
                                  </Form.Group>
                                  
                                  <Form.Group className="mb-3">
                                    <Form.Label>Email Body</Form.Label>
                                    <Form.Control
                                      as="textarea"
                                      rows={6}
                                      value={lenderFormData.emailBody || ''}
                                      onChange={(e) => handleFieldChange(lender._id, 'emailBody', e.target.value)}
                                      placeholder="Hello {lenderName},&#10;&#10;Please see the attached submission with supporting documentation for review. Thank you.&#10;&#10;{dealNotes}&#10;&#10;Submission Notes:&#10;&#10;Thank You,&#10;Operations Support"
                                    />
                                  </Form.Group>
                                </Col>
                              </Row>
                            </Form>
                          </Card.Body>
                        </div>
                      </Collapse>
                    </Card>
                  );
                })
              )}
            </div>
          )}
        </Col>
      </Row>
      
      {/* Add Lender Modal */}
      <AddLenderModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onLenderCreated={handleLenderCreated}
      />
      
      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteConfirm !== null} onHide={() => setShowDeleteConfirm(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {showDeleteConfirm && (
            <p>
              Are you sure you want to delete the lender "
              <strong>{lenders.find(l => l._id === showDeleteConfirm)?.lenderName}</strong>"?
              <br />
              <br />
              <span className="text-danger">This action cannot be undone.</span>
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={() => handleDeleteLender(showDeleteConfirm)}
          >
            <FaTrash /> Delete Lender
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default LenderSettings;